export const MORSE_LETTERS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
] as const

export const MORSE_DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'] as const

export const MORSE_SYMBOLS = ['.', ',', '?', '/', '=', '+', '@'] as const

export type MorseCharacter =
  | (typeof MORSE_LETTERS)[number]
  | (typeof MORSE_DIGITS)[number]
  | (typeof MORSE_SYMBOLS)[number]

export const LESSON_IDS = [
  'lesson-1',
  'lesson-2',
  'lesson-3',
  'lesson-4',
  'lesson-5',
  'lesson-6',
  'lesson-7',
  'lesson-8',
] as const

export type LessonId = (typeof LESSON_IDS)[number]

export interface PassCriteria {
  /** A single session must contain at least this many answers. */
  minAttempts: number
  /** A value from 0 to 1. For example, 0.8 means 80%. */
  minAccuracy: number
  /** Ready-to-display Japanese explanation of the criteria. */
  description: string
}

export interface LessonDefinition {
  id: LessonId
  level: number
  title: string
  description: string
  /** Characters introduced by this level. Empty for review levels. */
  newCharacters: readonly MorseCharacter[]
  /** Complete character pool used by this level. */
  characters: readonly MorseCharacter[]
  passCriteria: PassCriteria
}

const LEVEL_1 = ['E', 'T'] as const satisfies readonly MorseCharacter[]
const LEVEL_2_NEW = ['I', 'A', 'N', 'M'] as const satisfies readonly MorseCharacter[]
const LEVEL_2 = [...LEVEL_1, ...LEVEL_2_NEW] as const
const LEVEL_3_NEW = ['S', 'U', 'R', 'W', 'D', 'K', 'G', 'O'] as const satisfies readonly MorseCharacter[]
const LEVEL_3 = [...LEVEL_2, ...LEVEL_3_NEW] as const
const LEVEL_4_NEW = ['H', 'V', 'F', 'L', 'P', 'J'] as const satisfies readonly MorseCharacter[]
const LEVEL_4 = [...LEVEL_3, ...LEVEL_4_NEW] as const
const LEVEL_5_NEW = ['B', 'X', 'C', 'Y', 'Z', 'Q'] as const satisfies readonly MorseCharacter[]

export const LESSONS = [
  {
    id: 'lesson-1',
    level: 1,
    title: '最初の2符号',
    description: '短点だけの E（・）と長点だけの T（－）で、押す長さの違いに慣れましょう。',
    newCharacters: LEVEL_1,
    characters: LEVEL_1,
    passCriteria: {
      minAttempts: 12,
      minAccuracy: 0.75,
      description: '1回の練習で12問以上、正答率75%以上',
    },
  },
  {
    id: 'lesson-2',
    level: 2,
    title: '2要素の文字',
    description: 'I・A・N・Mを加え、短点と長点を2つ組み合わせる感覚を身につけます。',
    newCharacters: LEVEL_2_NEW,
    characters: LEVEL_2,
    passCriteria: {
      minAttempts: 18,
      minAccuracy: 0.8,
      description: '1回の練習で18問以上、正答率80%以上',
    },
  },
  {
    id: 'lesson-3',
    level: 3,
    title: '3要素の文字',
    description: 'S・U・R・W・D・K・G・Oを加え、3要素までの英字をリズムで打ち分けます。',
    newCharacters: LEVEL_3_NEW,
    characters: LEVEL_3,
    passCriteria: {
      minAttempts: 24,
      minAccuracy: 0.82,
      description: '1回の練習で24問以上、正答率82%以上',
    },
  },
  {
    id: 'lesson-4',
    level: 4,
    title: '4要素の文字・前半',
    description: 'H・V・F・L・P・Jを加えます。長い符号も、要素を数えずまとまりのあるリズムで打ちましょう。',
    newCharacters: LEVEL_4_NEW,
    characters: LEVEL_4,
    passCriteria: {
      minAttempts: 32,
      minAccuracy: 0.85,
      description: '1回の練習で32問以上、正答率85%以上',
    },
  },
  {
    id: 'lesson-5',
    level: 5,
    title: '英字を完成',
    description: 'B・X・C・Y・Z・Qを加え、A〜Zを完成します。似た符号の違いを意識して定着させます。',
    newCharacters: LEVEL_5_NEW,
    characters: MORSE_LETTERS,
    passCriteria: {
      minAttempts: 40,
      minAccuracy: 0.88,
      description: '1回の練習で40問以上、正答率88%以上',
    },
  },
  {
    id: 'lesson-6',
    level: 6,
    title: '数字',
    description: '0〜9に集中します。5要素の規則的な並びを、ひとかたまりのリズムとして覚えましょう。',
    newCharacters: MORSE_DIGITS,
    characters: MORSE_DIGITS,
    passCriteria: {
      minAttempts: 30,
      minAccuracy: 0.85,
      description: '1回の練習で30問以上、正答率85%以上',
    },
  },
  {
    id: 'lesson-7',
    level: 7,
    title: '記号',
    description: '通信でよく使う句読点・区切り・アットマークを加え、文字以外の長い符号に慣れます。',
    newCharacters: MORSE_SYMBOLS,
    characters: MORSE_SYMBOLS,
    passCriteria: {
      minAttempts: 28,
      minAccuracy: 0.85,
      description: '1回の練習で28問以上、正答率85%以上',
    },
  },
  {
    id: 'lesson-8',
    level: 8,
    title: '総合チャレンジ',
    description: '英字・数字・記号をすべて混ぜた実践練習です。安定したリズムと正確さで修了を目指します。',
    newCharacters: [],
    characters: [...MORSE_LETTERS, ...MORSE_DIGITS, ...MORSE_SYMBOLS],
    passCriteria: {
      minAttempts: 50,
      minAccuracy: 0.9,
      description: '1回の練習で50問以上、正答率90%以上',
    },
  },
] as const satisfies readonly LessonDefinition[]

export function getLessonById(id: LessonId): LessonDefinition {
  const lesson = LESSONS.find((candidate) => candidate.id === id)

  // LessonId is derived from the fixed curriculum, so this only guards future edits.
  if (!lesson) {
    throw new Error(`Unknown lesson: ${id}`)
  }

  return lesson
}
